import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Globe } from '@phosphor-icons/react';
import api from '../../services/api';
import BrandLogo from '../../components/BrandLogo';
import DemoBanner from '../../components/DemoBanner';
import { useSystemInfo } from '../../hooks/useSystemInfo';

const STATUS_LABELS = {
  new_order: 'New Order', order_submitted: 'Order Submitted', ready_to_load: 'Ready to Load',
  loaded: 'Loaded', shipped: 'Shipped', arrived_alg: 'Arrived in Algeria',
  cleared_port: 'Cleared from Port', delivered: 'Delivered', cancelled: 'Cancelled',
  active: 'Active', draft: 'Draft', closed: 'Closed',
};

const STATUS_COLORS = {
  new_order: 'bg-blue-100 text-blue-800', order_submitted: 'bg-indigo-100 text-indigo-800',
  ready_to_load: 'bg-amber-100 text-amber-800', loaded: 'bg-purple-100 text-purple-800',
  shipped: 'bg-violet-600 text-white', arrived_alg: 'bg-teal-100 text-teal-800',
  cleared_port: 'bg-cyan-100 text-cyan-800', delivered: 'bg-green-600 text-white',
  cancelled: 'bg-red-100 text-red-800', active: 'bg-emerald-100 text-emerald-800',
};

const TIMELINE_STEPS = [
  { key: 'new_order', label: 'New', short: '1' },
  { key: 'order_submitted', label: 'Submitted', short: '2' },
  { key: 'ready_to_load', label: 'Ready', short: '3' },
  { key: 'loaded', label: 'Loaded', short: '4' },
  { key: 'shipped', label: 'Shipped', short: '5' },
  { key: 'arrived_alg', label: 'Arrived', short: '6' },
  { key: 'cleared_port', label: 'Cleared', short: '7' },
  { key: 'delivered', label: 'Delivered', short: '8' },
];

const fmtDZD = (v) => v != null && v !== 0 ? `${Number(v).toLocaleString()} DA` : '-';

const TrackingTimeline = ({ status }) => {
  const idx = TIMELINE_STEPS.findIndex(s => s.key === status);
  return (
    <div className="flex items-center w-full gap-0" data-testid="tracking-timeline">
      {TIMELINE_STEPS.map((step, i) => (
        <React.Fragment key={step.key}>
          <div className="flex flex-col items-center gap-1 flex-shrink-0">
            <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold transition-all ${
              i < idx ? 'bg-green-500 text-white' : i === idx ? 'bg-primary text-white scale-110' : 'bg-gray-200 text-gray-400'
            }`}>
              {i < idx ? '\u2713' : step.short}
            </div>
            <span className={`text-[8px] sm:text-[10px] text-center leading-tight ${i === idx ? 'font-bold text-primary' : 'text-muted-foreground'}`}>{step.label}</span>
          </div>
          {i < TIMELINE_STEPS.length - 1 && (
            <div className={`flex-1 h-0.5 min-w-[8px] mt-[-12px] ${i < idx ? 'bg-green-500' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const PublicTrackingPage = () => {
  const { t, i18n } = useTranslation();
  const { brand_name } = useSystemInfo();
  const [orderNumber, setOrderNumber] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  // Auto-detect browser language
  useEffect(() => {
    const browserLang = navigator.language?.slice(0, 2);
    if (['fr', 'ar', 'zh'].includes(browserLang)) {
      i18n.changeLanguage(browserLang);
    }
  }, [i18n]);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!orderNumber || !identifier) { setError('Please enter both fields'); return; }
    setLoading(true);
    setError('');
    setData(null);
    try {
      const res = await api.post(`/track/verify?order_number=${encodeURIComponent(orderNumber.trim())}&identifier=${encodeURIComponent(identifier.trim())}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Order not found or verification failed');
    } finally { setLoading(false); }
  };

  const languages = [
    { code: 'en', label: 'EN' },
    { code: 'fr', label: 'FR' },
    { code: 'ar', label: 'AR' },
  ];

  return (
    <div className="min-h-screen bg-slate-50" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      <DemoBanner />
      {/* Header */}
      <header className="bg-white border-b border-border px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BrandLogo size={32} />
          <span className="font-bold text-sm">{brand_name}</span>
        </div>
        <Select value={i18n.language} onValueChange={(v) => i18n.changeLanguage(v)}>
          <SelectTrigger className="w-[70px] h-8 text-xs">
            <Globe className="w-3 h-3 mr-1" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            {languages.map(l => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 sm:py-10">
        {!data ? (
          /* Search Form */
          <div className="bg-white border-2 border-border p-5 sm:p-8" data-testid="track-form">
            <div className="text-center mb-6">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight mb-1">Track Your Order</h1>
              <p className="text-sm text-muted-foreground">Enter your order number and phone or NIN to verify</p>
            </div>
            <form onSubmit={handleTrack} className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Order Number</Label>
                <Input value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="e.g. 360-202605-0001" className="h-11 text-base font-mono" data-testid="track-order-number" />
              </div>
              <div>
                <Label className="text-sm font-medium">Phone Number or NIN</Label>
                <Input value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="Your phone or NIN number" className="h-11 text-base" data-testid="track-identifier" />
              </div>
              {error && <p className="text-sm text-red-600 bg-red-50 p-2 border border-red-200" data-testid="track-error">{error}</p>}
              <Button type="submit" disabled={loading} className="w-full h-11 text-base" data-testid="track-submit">
                {loading ? 'Verifying...' : 'Track Order'}
              </Button>
            </form>
          </div>
        ) : (
          /* Results */
          <div className="space-y-4" data-testid="track-results">
            {/* Back button */}
            <button onClick={() => setData(null)} className="text-sm text-primary hover:underline" data-testid="track-back">&larr; Search another order</button>

            {/* Order Header */}
            <div className="bg-white border-2 border-border p-4 sm:p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg sm:text-xl font-bold font-mono">{data.order_number}</h2>
                <Badge className={`${STATUS_COLORS[data.status] || 'bg-gray-100'} text-xs`}>
                  {STATUS_LABELS[data.status] || data.status}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mb-4">{data.client_name} &middot; {data.created_at?.slice(0, 10)}</p>

              {/* Timeline */}
              <TrackingTimeline status={data.status} />
            </div>

            {/* Cars */}
            {data.cars?.map((car, i) => (
              <div key={car.id || i} className="bg-white border-2 border-border p-4 sm:p-5" data-testid={`track-car-${i}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-base">{car.brand} {car.model}</h3>
                    <div className="flex gap-2 mt-1 flex-wrap">
                      {car.color && <span className="text-xs bg-slate-100 px-2 py-0.5">{car.color}</span>}
                      {car.year && <span className="text-xs bg-slate-100 px-2 py-0.5">{car.year}</span>}
                      {car.condition && <span className="text-xs bg-slate-100 px-2 py-0.5 capitalize">{car.condition}</span>}
                      {car.transmission && <span className="text-xs bg-slate-100 px-2 py-0.5">{car.transmission === 'manual' ? 'Manual' : car.transmission === 'auto_cvt' ? 'CVT' : 'DCT'}</span>}
                    </div>
                  </div>
                  {car.vin && <span className="text-[10px] font-mono bg-amber-50 border border-amber-200 px-2 py-0.5">VIN: {car.vin}</span>}
                </div>

                {/* End Client */}
                <div className="bg-slate-50 p-3 mb-3 text-sm space-y-0.5">
                  <div className="flex justify-between"><span className="text-muted-foreground">End Client</span><span className="font-medium">{car.end_client_name || '-'}</span></div>
                  {car.end_client_nin && <div className="flex justify-between"><span className="text-muted-foreground">NIN</span><span className="font-mono text-xs">{car.end_client_nin}</span></div>}
                </div>

                {/* Pricing & Payments */}
                <div className="border-t pt-3 space-y-1.5">
                  {car.selling_price > 0 && (
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Total Price</span><span className="font-bold">{fmtDZD(car.selling_price)}</span></div>
                  )}
                  {car.payments?.map(p => (
                    <div key={p.payment_number} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Payment {p.payment_number} ({p.payment_method === 'ccp' ? 'CCP' : 'Cash'} - {p.date})</span>
                      <span className="text-green-600 font-medium">{fmtDZD(p.amount)}</span>
                    </div>
                  ))}
                  {car.selling_price > 0 && (
                    <div className="flex justify-between text-sm pt-1 border-t">
                      <span className="font-semibold">Remaining</span>
                      <span className={`font-bold text-base ${car.remaining > 0 ? 'text-red-600' : 'text-green-600'}`}>{fmtDZD(car.remaining)}</span>
                    </div>
                  )}
                </div>

                {/* Shipping info */}
                {(car.bl_number || car.container_number) && (
                  <div className="border-t mt-3 pt-3 grid grid-cols-2 gap-2 text-sm">
                    {car.container_number && <div><span className="text-muted-foreground text-xs block">Container</span><span className="font-mono text-xs font-bold">{car.container_number}</span></div>}
                    {car.bl_number && <div><span className="text-muted-foreground text-xs block">BL #</span><span className="font-mono text-xs font-bold">{car.bl_number}</span></div>}
                    {car.vessel_number && <div><span className="text-muted-foreground text-xs block">Vessel</span><span className="text-xs">{car.vessel_number}</span></div>}
                    {car.eta && <div><span className="text-muted-foreground text-xs block">ETA</span><span className="text-xs font-medium">{car.eta}</span></div>}
                  </div>
                )}
              </div>
            ))}

            {/* Container Tracking */}
            {data.tracking?.length > 0 && (
              <div className="bg-white border-2 border-border p-4 sm:p-5">
                <h3 className="font-bold mb-3">Container Tracking</h3>
                {data.tracking.map(t => (
                  <div key={t.id} className="border p-3 mb-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-sm font-bold">{t.container_number}</span>
                      <Badge variant={t.status === 'succeeded' ? 'default' : 'secondary'} className="text-[10px]">{t.status}</Badge>
                    </div>
                    {t.tracking_data && (
                      <div className="text-sm space-y-0.5">
                        {t.tracking_data.vessel_name && <div className="flex justify-between"><span className="text-muted-foreground">Vessel</span><span>{t.tracking_data.vessel_name}</span></div>}
                        {t.tracking_data.port_of_lading && <div className="flex justify-between"><span className="text-muted-foreground">From</span><span>{t.tracking_data.port_of_lading}</span></div>}
                        {t.tracking_data.port_of_discharge && <div className="flex justify-between"><span className="text-muted-foreground">To</span><span>{t.tracking_data.port_of_discharge}</span></div>}
                        {t.tracking_data.pod_eta && <div className="flex justify-between"><span className="text-muted-foreground">ETA</span><span className="font-medium">{t.tracking_data.pod_eta?.slice(0, 10)}</span></div>}
                        {t.tracking_data.status && <div className="flex justify-between"><span className="text-muted-foreground">Status</span><span className="font-medium capitalize">{t.tracking_data.status?.replace(/_/g, ' ')}</span></div>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Footer */}
            <div className="text-center text-xs text-muted-foreground py-4">
              {brand_name}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicTrackingPage;
